import { IModelPrivatization } from "./IModelPrivatization";
import { StaticDataPrivatizationType } from "./enum/StaticDataPrivatizationType";
import { Subject, Observable } from "rxjs";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

export abstract class BaseModelPrivatization<I, K, V extends any>
  implements IModelPrivatization<K, V>
{
  abstract asModelPrivatized(label: string, K, I): V;
  abstract getBranchPrivate(branchCd: string): Map<K, V>;
  abstract getBranchPrivateKey(branchCd: string, ce?: V | null): K;

  abstract getBranchPrivateId(branchCd: string, ce?: V | null): I;
  abstract getBranchPrivateLabel(branchCd: string): string;
  abstract getPrivate(): Map<K, V>;
  abstract getPrivateKey(ce?: V | null): K;

  abstract getPrivateId(ce?: V | null): I;
  abstract getPrivateLabel(): string;
  abstract getRestricted(): Map<K, V>;
  abstract getRestrictedKey(ce?: V | null): K;

  abstract getRestrictedId(ce?: V | null): I;
  abstract getRestrictedLabel(): string;

  private idNames = new Map<string, K>();
  private clientSegments = new Map<string, K>();

  protected restrictedSubject: Subject<{ key: K; value: V }> = new Subject<{
    key: K;
    value: V;
  }>();

  get restricted$(): Observable<{ key: K; value: V }> {
    return this.restrictedSubject.asObservable();
  }

  asModelRestrictedIdName(
    id: string,
    name: string,
    restrictedKey: K,
    restrictedValue: V,
  ): { key: K; value: V } {
    let restrictedIdName = null;

    if (
      restrictedValue == null ||
      typeof restrictedValue.constructor != "function" ||
      this.idNames.has(`id:${id}_name:${name}`)
    ) {
      return null;
    }

    restrictedIdName = {
      key: restrictedKey,
      value: Object.assign(new (restrictedValue as any).constructor(), restrictedValue),
    };

    return restrictedIdName;
  }

  asModelRestrictedClientSegment(
    clientSegment: string,
    restrictedKey: K,
    restrictedValue: V,
  ): { key: K; value: V } {
    let restrictedClientSegment = null;

    if (
      this.clientSegments.has(clientSegment) ||
      restrictedValue == null ||
      typeof restrictedValue.constructor != "function"
    ) {
      return null;
    }

    restrictedClientSegment = {
      key: restrictedKey,
      value: Object.assign(new (restrictedValue as any).constructor(), restrictedValue),
    };

    return restrictedClientSegment;
  }

  addRestrictedClientSegment(clientSegment: string, restrictedKey: K, restrictedValue: V): K {
    let restrictedClientSegment = this.asModelRestrictedClientSegment(
      clientSegment,
      restrictedKey,
      restrictedValue,
    );

    StaticDataPrivatizationType.setPrivatization(
      restrictedClientSegment.value,
      StaticDataPrivatizationType.RestrictedClientSegment,
    );

    this.clientSegments.set(clientSegment, restrictedClientSegment.key);
    this.restrictedSubject.next(restrictedClientSegment);

    return restrictedClientSegment.key;
  }

  addRestrictedIdName(id: string, name: string, restrictedKey: K, restrictedValue: V): K {
    let restrictedIdName = this.asModelRestrictedIdName(id, name, restrictedKey, restrictedValue);

    StaticDataPrivatizationType.setPrivatization(
      restrictedIdName.value,
      StaticDataPrivatizationType.RestrictedClientSegment,
    );

    this.idNames.set(`id:${id}_name:${name}`, restrictedIdName.key);
    this.restrictedSubject.next(restrictedIdName);

    return restrictedIdName.key;
  }

  getRestrictedClientSegmentKey(clientSegment: string): K {
    return this.clientSegments.get(clientSegment);
  }

  getRestrictedIdNameKey(id: string, name: string): K {
    return this.idNames.get(`id:${id}_name:${name}`);
  }

  hasRestrictedClientSegment(clientSegment: string): boolean {
    return this.clientSegments.has(clientSegment);
  }

  hasRestrictedIdName(id: string, name: string): boolean {
    return this.idNames.has(`id:${id}_name:${name}`);
  }

  protected hashClientSegment(clientSegment: string) {
    let hash = 0;

    if (clientSegment == null || typeof clientSegment !== "string" || clientSegment.length === 0) {
      return hash;
    }

    for (let i = 0; i < clientSegment.length; i++) {
      let c = clientSegment.charCodeAt(i);
      hash = (hash << 5) - hash + c;
      hash = hash & hash;
    }

    return hash;
  }
}
